import { useCallback, useEffect, useRef, useState } from 'react';

import { Toast } from '@opendoor/bricks-next';

type ToastType = 'error' | 'info' | 'success';

export type ShowToastFn = (options: ToastOptions) => void;

export type ToastOptions = {
  /**
   * The type of the toast - affects styling
   */
  type: ToastType;

  /**
   * The message to display
   */
  message: string;

  /**
   * Used for tracking impressions
   */
  analyticsName: string;

  /**
   * Delay in ms before the toast becomes visible
   *
   * Default 0 ms
   */
  openDelayMs?: number;

  /**
   * Auto-close duration in ms
   *
   * Default 3,000 ms
   */
  autoCloseMs?: number;
};

const DEFAULT_AUTO_CLOSE_MS = 3_000;

/**
 * Given defaults `D`, computes what `showToast` still requires:
 * - Fields not in `D` keep their original optionality from `ToastOptions`
 * - Fields in `D` become optional overrides
 */
type ShowToastArg<D extends Partial<ToastOptions>> = Omit<ToastOptions, keyof D> &
  Partial<Pick<ToastOptions, Extract<keyof D, keyof ToastOptions>>>;

type ToastState = {
  isOpen: boolean;
  message: string;
  type: ToastType;
  analyticsName: string;
  duration: number;
};

/**
 * Imperative bridge over bricks-next's declarative Toast component.
 *
 * Augments the bricks-next Toast component to allow delayed opening.
 *
 * Accepts optional defaults so that `showToast` only requires the
 * fields not already provided:
 *
 * ```ts
 * const { showToast } = useToast({ type: 'error', analyticsName: 'my-toast' });
 * showToast({ message: 'Something went wrong' });
 * ```
 *
 * Default values are overridden when new values are passed to `showToast`.
 */
export const useToast = <D extends Partial<ToastOptions>>(defaults?: D) => {
  const [state, setState] = useState<ToastState>({
    isOpen: false,
    message: '',
    type: 'error',
    analyticsName: '',
    duration: DEFAULT_AUTO_CLOSE_MS,
  });

  const defaultsRef = useRef(defaults);
  const delayRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(delayRef.current);
  }, []);

  const showToast = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- specifically used to check if an empty object suffices
      ...args: {} extends ShowToastArg<D> ? [options?: ShowToastArg<D>] : [options: ShowToastArg<D>]
    ) => {
      const merged = { ...defaultsRef.current, ...(args[0] ?? {}) } as ToastOptions;
      clearTimeout(delayRef.current);
      const duration = merged.autoCloseMs ?? DEFAULT_AUTO_CLOSE_MS;

      const open = () => {
        setState({
          isOpen: true,
          message: merged.message,
          type: merged.type,
          analyticsName: merged.analyticsName,
          duration,
        });
      };

      if (merged.openDelayMs) {
        delayRef.current = setTimeout(open, merged.openDelayMs);
      } else {
        open();
      }
    },
    [],
  );

  const hideToast = useCallback(() => {
    clearTimeout(delayRef.current);
    setState((s) => ({ ...s, isOpen: false }));
  }, []);

  const ToastElement = (
    <Toast
      isOpen={state.isOpen}
      message={state.message}
      hasError={state.type === 'error'}
      isSuccess={state.type === 'success'}
      duration={state.duration}
      analyticsName={state.analyticsName}
      onClose={hideToast}
      onOpenChange={(open) => {
        if (!open) {
          hideToast();
        }
      }}
    />
  );

  return { showToast, hideToast, ToastElement };
};
